/*
 * svnty-header.js — the header's two behaviours Trade does not have (SVNTY, 2026-09-19).
 * Classic deferred asset. No deps, ASCII only, no console.
 *
 * WHAT IT DOES
 *  1. THE LOCK. Trade locks the page behind an open drawer with `overflow: hidden` on <body> alone.
 *     That is not a lock on iOS (the document element keeps scrolling) and on a pointer-fine window
 *     under 990px the vanishing scrollbar jumps the whole layout sideways. This mirrors Trade's own
 *     class onto <html>, compensates the scrollbar width, and pins the scroll offset — so the page
 *     behind the drawer cannot move and nothing reflows.
 *     THE ONE HOLDER: Trade's `overflow-hidden-*` class IS the guard. The sibling paid for the alternative
 *     (assets/sib-wishlist-nudge.js:113, GUARDS.md BUG-08: four of seven lock holders released the
 *     shared lock without checking whether another surface still held it). One holder, one release.
 *     THE ONE LOCK (D6-17, R206's tail): the lock is ONE class write on <html> in the frame the door opens:
 *     `svnty-locked` (the page held) and the headstack's `svnty-parked` (the bar away, the root white, the
 *     curtain up; svnty-headstack.js claim() prepares its parts and names the class) land in one
 *     classList.add, and every other consequence is CSS derived from those two classes; on a touch screen the
 *     body's pan refusal (`svnty-held`, a whole-page restyle in WebKit) follows one frame after the door has
 *     painted, and leaves in the same write as the lock. Nothing is read
 *     between the writes: the held spot is the last scroll event's (a cache), refined after the frame
 *     has painted; the scrollbar gutter is measured while the page is free (load, resize, the press
 *     before a click) and lives on the root as a constant, so the lock writes no length.
 *  2. THE FOCUS. Trade hands focus to the drawer's <summary> on open, which in SVNTY is the animated
 *     close mark — the iOS law (TRAPS IOS-20: focus the close control, never a field, or the keyboard
 *     covers the sheet). This only asserts it, in case the summary is not focusable yet on first paint.
 *
 * WHAT IT MUST NEVER DO
 *  Measure the sticky header, or write a length into it. WebKit's scrolling thread owns that layer and
 *  reconciles main-thread writes only when the gesture ENDS (TRAPS IOS-01/02/03). Heights are CSS math
 *  over `svh` in svnty-header.css.
 */
(function () {
  'use strict';
  if (window.__svntyHeaderInit) return;
  window.__svntyHeaderInit = true;

  var LOCK = 'svnty-locked', HELD = 'svnty-held';
  var CLASSES = ['overflow-hidden', 'overflow-hidden-mobile', 'overflow-hidden-tablet', 'overflow-hidden-desktop'];
  var root = document.documentElement;
  var locked = false;
  var savedY = 0;

  function bodyWantsLock() {
    for (var i = 0; i < CLASSES.length; i++) {
      if (document.body.classList.contains(CLASSES[i])) return true;
    }
    return false;
  }

  /* the scrollbar's width, read only while the page is free (the press before a click, a resize, the load) and kept on the
     root as a constant: html.svnty-locked pads by it (svnty-header.css), so the lock itself writes no length (D6-17) */
  var gutterW = -1;
  function gutter() {
    if (locked) return;
    var w = window.innerWidth - root.clientWidth, g = w > 0 && w < 40 ? w : 0;
    if (g === gutterW) return;
    gutterW = g;
    if (g) root.style.setProperty('--svnty-lock-gutter', g + 'px'); else root.style.removeProperty('--svnty-lock-gutter');
  }
  /* THE HELD SPOT (D6-17): the page's offset as the last scroll event and the last press left it (the press that precedes
     every click or key that opens a door: layout is clean then, so the read is free); the lock never asks the page for it
     (a scroll offset read after Trade's body class forces WebKit to lay the whole page out in the middle of the open), and
     it is the spot BEFORE the door: the door's own focus may scroll the held page under it, and the close puts it back */
  var curY = window.pageYOffset || 0;
  window.addEventListener('scroll', function () { if (!locked) curY = window.pageYOffset || 0; }, { passive: true });

  /* THE GUARD (D2-10 / R104): on a touch device the root's overflow is never written (svnty-header.css
     THE LOCK v3), so a touch that is not inside a panel's own scroller - or that would push a scroller
     past its end - is cancelled here before the page can move. Non-passive by necessity; it only runs
     while a lock is held. */
  var COARSE = !!(window.matchMedia && window.matchMedia('(pointer: coarse)').matches);
  var touchY = 0, touchX = 0;
  function scrollerOf(el) {
    while (el && el !== document.body && el !== root) {
      if (el.nodeType === 1) {
        var cs = getComputedStyle(el), oy = cs.overflowY;
        if ((oy === 'auto' || oy === 'scroll') && el.scrollHeight > el.clientHeight + 1) return el;
      }
      el = el.parentNode;
    }
    return null;
  }
  /* D8-4 THE RAIL INSIDE A DOOR (R233, on the iPhone: "the Picked for you carousel at the top of the search sheet gets
     stuck and frozen"): a horizontal scroller (a rail) between the finger and the door's vertical scroller. The guard below
     used to cancel a swipe on a rail that sat at the top of the sheet the moment the finger drifted a pixel down (the
     vertical scroller was at its top), and a cancelled touchmove kills the whole gesture, the pan with it. */
  function railOf(el, stop) {
    while (el && el !== stop && el !== document.body && el !== root) {
      if (el.nodeType === 1) {
        var cs = getComputedStyle(el), ox = cs.overflowX;
        if ((ox === 'auto' || ox === 'scroll') && el.scrollWidth > el.clientWidth + 1) return el;
      }
      el = el.parentNode;
    }
    return null;
  }
  /* S-24 THE SLOP AND THE LATCH (SHEET_S_MISC, 2026-09-26): the axis was judged on every move from the first one, so a
     sideways swipe whose first 16 ms read steeper than 45 degrees (dx 1, dy 2) was cancelled at the sheet's top and WebKit
     dropped the whole gesture, the rail's pan with it: R233's freeze, now and then. On a rail the guard now waits until the
     finger has travelled 8 px from where it landed, judges the axis once, and keeps that answer for the whole gesture: a
     sideways gesture is never cancelled, a vertical one meets the rule below. Off a rail nothing changed. */
  var SLOP = 8, axis = '';
  function onTouchStart(e) {
    var t = e.touches && e.touches.length ? e.touches[0] : null;
    touchY = t ? t.clientY : 0; touchX = t ? t.clientX : 0;
    axis = '';
  }
  function onTouchMove(e) {
    if (!locked || !e.cancelable) return;
    if (e.touches && e.touches.length > 1) return; /* pinch stays the browser's */
    var sc = scrollerOf(e.target);
    var y = e.touches[0].clientY, dy = y - touchY, dx = e.touches[0].clientX - touchX;
    if (railOf(e.target, sc)) {
      if (!axis) {
        if (Math.max(Math.abs(dx), Math.abs(dy)) < SLOP) return; /* too short to say: the gesture stays alive */
        axis = Math.abs(dx) >= Math.abs(dy) ? 'x' : 'y';
      }
      if (axis === 'x') return; /* a sideways gesture on a rail is the rail's: the browser pans it */
    }
    if (!sc) { e.preventDefault(); return; }
    var atTop = sc.scrollTop <= 0, atEnd = sc.scrollTop + sc.clientHeight >= sc.scrollHeight - 1;
    if ((dy > 0 && atTop) || (dy < 0 && atEnd)) e.preventDefault();
  }
  /* THE SETTLE: the panel's class comes off at the START of its close animation, and a finger's momentum
     can move the page in the 400 ms that follow (measured on the iPhone, R99 run 2: ~130 px). For half a
     second after an unlock the page is held at the spot it was locked at. */
  var settleUntil = 0;
  function onSettleScroll() {
    if (now() > settleUntil) { window.removeEventListener('scroll', onSettleScroll); return; }
    if (Math.abs((window.pageYOffset || 0) - savedY) > 2) window.scrollTo(0, savedY);
  }
  function now() { return Date.now ? Date.now() : new Date().getTime(); }

  /* THE KEYBOARD HOLD (2026-09-24, the size sheet's fields: "do not move the page when I tap a cell"): iOS pushes
     the layout viewport up to keep a focused field above its keyboard even when the page is locked under a sheet. While
     locked, every visual-viewport resize (the keyboard) puts the page back at the held spot; the field lives in the
     sheet's own scroller, which is the box that should move. */
  function onKeyboard() { if (locked && Math.abs((window.pageYOffset || 0) - savedY) > 2) window.scrollTo(0, savedY); }
  if (window.visualViewport) window.visualViewport.addEventListener('resize', onKeyboard, { passive: true });
  /* P22-3 THE SHEET FOLLOWS THE EYE (the owner, 2026-10-02, the iPhone: "when I click on the search bar and the keyboard comes up, the
     search bar cell gets pushed up and cropped"). With the keyboard up iOS pans the VISUAL viewport inside the layout viewport to seat
     the focused field at its top edge (11 px on the search sheet: the field's own offset; no box around the field can scroll and a page
     scroll never moves the sheet, both measured in the iPhone's own Safari). The header's sheets hang from the layout viewport, so the
     field lost its top edge under the status bar. While a sheet holds the page the pan is published as --svnty-vv-top and the sheet's
     top follows it (assets/svnty-search.css, assets/svnty-header.css); a pinch (scale over 1) is the shopper's own and is left alone. */
  var panTop = 0;
  function onViewportPan() {
    var vv = window.visualViewport;
    var t = locked && vv && !(vv.scale > 1.01) ? Math.max(0, Math.round((vv.offsetTop || 0) * 2) / 2) : 0;
    if (t === panTop) return;
    panTop = t;
    if (t) root.style.setProperty('--svnty-vv-top', t + 'px'); else root.style.removeProperty('--svnty-vv-top');
  }
  if (window.visualViewport) {
    window.visualViewport.addEventListener('scroll', onViewportPan, { passive: true });
    window.visualViewport.addEventListener('resize', onViewportPan, { passive: true });
  }
  function lock() {
    if (locked) return;
    locked = true;
    savedY = curY;
    /* THE ONE WRITE: the headstack readies its parts (the curtain's place, the doors' slab height, the status colour) and
       names its class; both classes land in one classList.add, so the page restyles once, in the frame that shows the door */
    var hs = window.__svntyHeadstack, extra = hs && hs.claim ? hs.claim() : null;
    root.classList.add.apply(root.classList, [LOCK].concat(extra || []));
    if (COARSE) {
      document.addEventListener('touchstart', onTouchStart, { passive: true });
      document.addEventListener('touchmove', onTouchMove, { passive: false });
      /* the body's pan refusal (a whole-page restyle in WebKit) lands the frame after the door has painted */
      var at = ++lockSeq;
      requestAnimationFrame(function () { requestAnimationFrame(function () { if (locked && at === lockSeq) root.classList.add(HELD); }); });
    }
  }
  var lockSeq = 0;

  function unlock() {
    if (!locked) return;
    locked = false;
    lockSeq++;
    onViewportPan(); /* P22-3: the sheet is gone, its pan offset with it */
    root.classList.remove(LOCK, HELD);
    var hs = window.__svntyHeadstack;
    if (hs && hs.release) hs.release();
    if (COARSE) {
      document.removeEventListener('touchstart', onTouchStart, { passive: true });
      document.removeEventListener('touchmove', onTouchMove, { passive: false });
      settleUntil = now() + 500;
      window.addEventListener('scroll', onSettleScroll, { passive: true });
    }
    /* the page never moved, so nothing to restore on a class-only lock; guard the edge where a
       browser did scroll the document while it was hidden */
    var y = window.pageYOffset || 0;
    if (Math.abs(y - savedY) > 2) {
      window.scrollTo(0, savedY);
    }
    curY = savedY;
  }

  function sync() {
    bodyWantsLock() ? lock() : unlock();
  }

  if (typeof MutationObserver === 'function') {
    new MutationObserver(sync).observe(document.body, { attributes: true, attributeFilter: ['class'] });
  }
  /* the held spot and the gutter: at load, on a resize, and on the press that precedes every click and key that opens a door */
  function atPress() {
    if (locked) return;
    curY = window.pageYOffset || 0;
    if (!COARSE) gutter();
  }
  if (!COARSE) {
    gutter();
    window.addEventListener('load', gutter);
    window.addEventListener('resize', gutter, { passive: true });
  }
  document.addEventListener('pointerdown', atPress, { capture: true, passive: true });
  document.addEventListener('keydown', function (e) { if (e.key === 'Enter' || e.key === ' ') atPress(); }, true);
  sync();

  /* 2. THE FOCUS — assert the close mark on open, once per open */
  document.addEventListener(
    'click',
    function (e) {
      var summary = e.target && e.target.closest && e.target.closest('.header__icon--menu');
      if (!summary) return;
      var details = summary.parentNode;
      window.setTimeout(function () {
        if (details && details.hasAttribute && details.hasAttribute('open') && document.activeElement !== summary) {
          try {
            summary.focus({ preventScroll: true });
          } catch (err) {
            /* older Safari: no options bag */
          }
        }
      }, 120);
    },
    true
  );

  /* ------------------------------------------------------------------------ *
   * 3. THE MEGA PANEL'S ANNOUNCEMENT AND ITS DISMISSAL (R3-15, round 3)
   *
   * The panel opens on `:hover` / `:focus-within` in CSS and that stays: no state machine, no JS
   * dependency for the open itself. What was missing is everything a shopper who is not using a
   * mouse needs. Measured at 1440 before this: focusing "New in" opened the panel (visibility
   * visible, 9 links, Tab entered them) while the trigger reported `aria-expanded === null` and
   * `aria-haspopup === null`, and Escape left the panel open with focus still inside it — a WCAG
   * 2.1 SC 1.4.13 (Dismissible) failure plus a 4.1.2 gap.
   *
   * `aria-expanded` is mirrored from what the CSS is actually doing (pointer over the item, or
   * focus inside it). Escape adds `is-dismissed`, which out-ranks the two open rules, and returns
   * focus to the trigger; leaving the item clears it, so a dismissal never outlives the visit.
   * ------------------------------------------------------------------------ */
  var PANEL_ITEM = '.svnty-navm__item--panel';

  function panelTrigger(item) {
    return item && item.querySelector(':scope > .svnty-navm__link');
  }

  function setPanelState(item, open) {
    var trigger = panelTrigger(item);
    if (trigger) trigger.setAttribute('aria-expanded', open ? 'true' : 'false');
  }

  function openPanel(item) {
    if (!item || (window.SVNTY && window.SVNTY.navMenu)) return;   /* D11-6: the machine owns the panels (svnty-nav-rail.js) */
    item.classList.remove('is-dismissed');
    setPanelState(item, true);
  }

  function closePanel(item, dismissed) {
    if (!item || (window.SVNTY && window.SVNTY.navMenu)) return;
    if (dismissed) item.classList.add('is-dismissed');
    else item.classList.remove('is-dismissed');
    setPanelState(item, false);
  }

  document.addEventListener('pointerover', function (e) {
    var item = e.target && e.target.closest && e.target.closest(PANEL_ITEM);
    if (item) openPanel(item);
  });

  document.addEventListener('pointerout', function (e) {
    var item = e.target && e.target.closest && e.target.closest(PANEL_ITEM);
    if (!item) return;
    var to = e.relatedTarget;
    if (to && item.contains(to)) return; /* still inside the item */
    if (item.contains(document.activeElement)) return; /* keyboard is still in it */
    closePanel(item, false);
  });

  document.addEventListener('focusin', function (e) {
    var item = e.target && e.target.closest && e.target.closest(PANEL_ITEM);
    if (item) {
      openPanel(item);
      return;
    }
    /* focus went somewhere else entirely: every panel closes and forgets its dismissal */
    var all = document.querySelectorAll(PANEL_ITEM + '.is-dismissed, ' + PANEL_ITEM + ' > [aria-expanded="true"]');
    for (var i = 0; i < all.length; i += 1) {
      closePanel(all[i].closest ? all[i].closest(PANEL_ITEM) : null, false);
    }
  });

  document.addEventListener('keydown', function (e) {
    if (e.key !== 'Escape' && e.keyCode !== 27) return;
    if (window.SVNTY && window.SVNTY.navMenu) return;   /* D11-6: the machine handles Escape */
    var item = e.target && e.target.closest && e.target.closest(PANEL_ITEM);
    if (!item) return;
    closePanel(item, true);
    var trigger = panelTrigger(item);
    if (trigger) {
      try {
        trigger.focus({ preventScroll: true });
      } catch (err) {
        trigger.focus();
      }
    }
    e.stopPropagation(); /* the drawer / sheet handlers are not this shopper's Escape */
  });
})();
